import type { OfflineBundle, OfflineRow } from './domainTypes';
import type {
  ConfiguracionLiquidacion,
  DatosLiquidacion,
  DatosPreparacion,
  LiquidacionSnapshot,
  ResultadoLiquidacion,
} from '../liquidacion';
import type {
  DiaEvolucion,
  EvolucionLiquidacion,
  PesoEvolucion,
  VentanaEvolucion,
} from '../liquidacion-evolucion';
import { Decimal } from './decimal';

export const row = (v: unknown): OfflineRow =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as OfflineRow) : {};
export const rows = (v: unknown): OfflineRow[] => (Array.isArray(v) ? (v as OfflineRow[]) : []);
export const number = (v: unknown): number => Number(v ?? 0);
export const text = (v: unknown): string => String(v ?? '');
export const decimal = (v: unknown): Decimal => new Decimal(text(v ?? 0));
export const sum = (rs: OfflineRow[], key: string): Decimal =>
  rs.reduce((a, r) => a.add(decimal(r[key])), new Decimal(0));
export const dateNumber = (value: unknown): number => {
  const str = text(value),
    parsed = Date.parse(`${str}T00:00:00Z`);
  if (
    !/^\d{4}-\d\d-\d\d$/.test(str) ||
    !Number.isFinite(parsed) ||
    new Date(parsed).toISOString().slice(0, 10) !== str
  )
    throw new Error('Fecha inválida');
  return parsed / 86400000;
};
export const dateAt = (day: number) => new Date(day * 86400000).toISOString().slice(0, 10);
/** Match Python round(float, ndigits), including binary ties such as 2.675. */
export function pythonRound(value: number, places: number): number {
  if (!Number.isFinite(value)) throw new Error('Número no finito');
  if (value === 0) return 0;
  const view = new DataView(new ArrayBuffer(8));
  view.setFloat64(0, value);
  const bits = view.getBigUint64(0),
    negative = bits >> 63n,
    exponent = Number((bits >> 52n) & 2047n),
    fraction = bits & ((1n << 52n) - 1n);
  const mantissa = (exponent === 0 ? fraction : fraction + (1n << 52n)) * (negative ? -1n : 1n),
    shift = (exponent === 0 ? -1022 : exponent - 1023) - 52;
  const exact =
    shift >= 0
      ? new Decimal(mantissa * 2n ** BigInt(shift))
      : new Decimal(`${mantissa * 5n ** BigInt(-shift)}e${shift}`);
  return exact.round(places).toNumber();
}
export function madridDate(value: Date | string = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(value));
  return `${parts.find((p) => p.type === 'year')!.value}-${parts.find((p) => p.type === 'month')!.value}-${parts.find((p) => p.type === 'day')!.value}`;
}
export function forCamada(bundle: OfflineBundle, cid: number) {
  const camada = bundle.tables.camadas.find((r) => r.id === cid);
  if (!camada) throw Object.assign(new Error('Camada no encontrada'), { status: 404 });
  const lotes = bundle.tables.lotes.filter((r) => number(r.camada_id) === cid),
    ids = new Set(lotes.map((r) => r.id));
  return {
    camada,
    lotes,
    entradas: bundle.tables.entradas.filter((r) => number(r.camada_id) === cid),
    registros: bundle.tables.registros.filter((r) => ids.has(number(r.lote_nave_id))),
    pesajes: bundle.tables.pesajes.filter((r) => ids.has(number(r.lote_nave_id))),
    entregas: bundle.tables.entregas.filter((r) => number(r.camada_id) === cid),
    salidas: bundle.tables.salidas.filter((r) => number(r.camada_id) === cid),
    observaciones: bundle.tables.observaciones.filter((r) => number(r.camada_id) === cid),
  };
}
export function configuredPoints(value: unknown): OfflineRow[] {
  return Array.isArray(value) ? rows(value) : rows(row(value).puntos);
}
export function savedLiquidation(bundle: OfflineBundle, cid: number): OfflineRow | null {
  const saved = bundle.tables.liquidaciones.find((r) => number(r.camada_id) === cid);
  if (!saved) return null;
  return saved.guardada ? row(saved.guardada) : saved;
}
function totalBajas(registros: OfflineRow[]): number {
  return registros.reduce((sum, r) => sum + number(r.bajas_muerte) + number(r.bajas_triaje), 0);
}
export function preparation(bundle: OfflineBundle, cid: number) {
  const { camada, registros, entregas, salidas } = forCamada(bundle, cid);
  const fechas = salidas.map((s) => text(s.fecha_carga)).sort();
  const datos: DatosPreparacion = {
    sexo: text(camada.sexo) as DatosPreparacion['sexo'],
    fecha_entrada: text(camada.fecha_entrada),
    fecha_final: fechas.at(-1) ?? null,
    edad_entrada: camada.edad_dias_entrada == null ? null : number(camada.edad_dias_entrada),
    aves_entradas: number(camada.num_animales_total),
    bajas_registradas: totalBajas(registros),
    kg_entrada: decimal(camada.kg_entrada_total).toString(),
    kg_salida:
      salidas.length && salidas.every((s) => s.kg_confirmados_destino != null)
        ? sum(salidas, 'kg_confirmados_destino').toString()
        : null,
    kg_pienso:
      entregas.length && camada.cerrada && camada.kg_sobrante_pienso != null
        ? sum(entregas, 'kg').sub(decimal(camada.kg_sobrante_pienso)).toString()
        : null,
    ajuste_carga_eur: decimal(camada.ajuste_carga_inicial_eur).toString(),
    bonificacion_eur: decimal(camada.bonificacion_inicial_eur).toString(),
  };
  return {
    datos,
    configuracion: bundle.catalogos.liquidacion as ConfiguracionLiquidacion,
    guardada: savedLiquidation(bundle, cid),
    local_pending: !!savedLiquidation(bundle, cid)?.local_pending,
    cerrada: !!camada.cerrada,
  };
}
export function calculateLiquidation(
  datos: DatosLiquidacion,
  config: ConfiguracionLiquidacion,
): ResultadoLiquidacion {
  if (!config?.sexos?.[datos.sexo]) throw new Error('Configuración de liquidación no disponible');
  const dias = dateNumber(datos.fecha_final) - dateNumber(datos.fecha_entrada),
    edad = datos.edad_entrada + dias;
  if (
    dias < 0 ||
    !Number.isInteger(datos.edad_entrada) ||
    datos.edad_entrada < 0 ||
    !Number.isInteger(datos.aves_entradas) ||
    datos.aves_entradas <= 0 ||
    !Number.isInteger(datos.bajas_registradas) ||
    datos.bajas_registradas < 0 ||
    datos.bajas_registradas > datos.aves_entradas
  )
    throw new Error('Datos de liquidación inválidos');
  const p = config.sexos[datos.sexo],
    tramo = p.tramos.find((t) => t.edad_min <= edad && (t.edad_max == null || edad < t.edad_max));
  if (!tramo) throw new Error('Tramos de liquidación inválidos');
  for (const field of ['kg_entrada', 'kg_salida', 'kg_pienso'] as const)
    if (decimal(datos[field]).compare(0) < 0) throw new Error('Los kg deben ser positivos');
  const kg = decimal(datos.kg_salida).sub(datos.kg_entrada);
  if (kg.compare(0) <= 0)
    throw new Error('Los kg de salida deben ser mayores que los kg de entrada');
  const bruto = decimal(p.base)
    .add(p.complemento)
    .add(decimal(p.coef_dias).mul(dias))
    .mul(kg)
    .sub(decimal(p.coef_conversion).mul(datos.kg_pienso))
    .div(1000);
  const bajas = decimal(tramo.porcentaje)
    .mul(datos.aves_entradas)
    .div(100)
    .sub(datos.bajas_registradas)
    .mul(p.coef_bajas);
  const iva = bruto.mul(config.porcentaje_iva).div(100);
  return {
    sexo: datos.sexo,
    dias_engorde: dias,
    edad_total: edad,
    kg_ganados: kg.toString(),
    toneladas_ganadas: kg.div(1000).toString(),
    conversion: decimal(datos.kg_pienso).div(kg).toString(),
    porcentaje_bajas: decimal(datos.bajas_registradas).mul(100).div(datos.aves_entradas).toString(),
    porcentaje_referencia: decimal(tramo.porcentaje).toString(),
    tramo_aplicado: tramo,
    bruto_eur: bruto.toString(),
    iva_eur: iva.toString(),
    importe_bajas_eur: bajas.toString(),
    importe_carga_eur: decimal(datos.ajuste_carga_eur).toString(),
    bonificacion_eur: decimal(datos.bonificacion_eur).toString(),
    total_eur: bruto
      .add(iva)
      .add(bajas)
      .add(datos.ajuste_carga_eur)
      .add(datos.bonificacion_eur)
      .toString(),
  };
}
export function preview(bundle: OfflineBundle, cid: number, request: unknown) {
  const input = row(request),
    prep = preparation(bundle, cid),
    saved = prep.guardada,
    snapshot = row(saved?.snapshot),
    mode = text(input.modo || 'nueva');
  if (mode === 'nueva' && saved) throw new Error('Ya existe una liquidación; utiliza su edición');
  if (mode !== 'nueva' && !saved) throw new Error('No existe una liquidación guardada que editar');
  if (!['nueva', 'guardada', 'actualizar_registros'].includes(mode))
    throw new Error('Modo de liquidación inválido');
  const fuentes = mode === 'guardada' ? row(snapshot.fuentes) : prep.datos;
  const base = mode === 'guardada' ? row(snapshot.datos) : { ...prep.datos };
  if (mode === 'actualizar_registros')
    Object.assign(base, {
      ajuste_carga_eur: row(snapshot.datos).ajuste_carga_eur,
      bonificacion_eur: row(snapshot.datos).bonificacion_eur,
    });
  const cambios = row(input.cambios),
    editable = [
      'fecha_final',
      'edad_entrada',
      'kg_entrada',
      'kg_salida',
      'kg_pienso',
      'ajuste_carga_eur',
      'bonificacion_eur',
    ];
  for (const [key, value] of Object.entries(cambios))
    if (!editable.includes(key) || value == null) throw new Error('Campo de liquidación inválido');
  const datos = { ...base, ...cambios } as unknown as DatosLiquidacion;
  for (const field of ['fecha_final', 'edad_entrada', 'kg_salida', 'kg_pienso'])
    if (row(datos)[field] == null) throw new Error(`Falta ${field}`);
  const configuracion = (
    mode === 'nueva' ? prep.configuracion : snapshot.configuracion
  ) as ConfiguracionLiquidacion;
  return {
    datos,
    fuentes,
    configuracion,
    resultado: calculateLiquidation(datos, configuracion),
    version_esperada: number(saved?.version),
    token_preview: null,
    local_preview: true,
  };
}
export function detail(bundle: OfflineBundle, cid: number, today: string) {
  const f = forCamada(bundle, cid),
    { camada } = f;
  const lotes = f.lotes
    .map((l) => {
      const registros = f.registros.filter((r) => number(r.lote_nave_id) === l.id),
        salidas = f.salidas
          .flatMap((s) => rows(s.lineas))
          .filter((line) => number(line.lote_nave_id) === l.id);
      return {
        lote_id: l.id,
        nave_id: l.nave_id,
        nave_codigo:
          bundle.tables.naves.find((n) => n.id === l.nave_id)?.codigo ?? l.nave_codigo ?? '?',
        num_animales_asignados: number(l.num_animales_asignados),
        aves_actuales: Math.max(
          number(l.num_animales_asignados) -
            totalBajas(registros) -
            sum(salidas, 'unidades_cargadas').toNumber(),
          0,
        ),
        registros_count: registros.length,
        pesajes_count: f.pesajes.filter((p) => number(p.lote_nave_id) === l.id).length,
      };
    })
    .sort((a, b) => text(a.nave_codigo).localeCompare(text(b.nave_codigo)));
  const bajas = totalBajas(f.registros),
    cargadas = sum(
      f.salidas.flatMap((s) => rows(s.lineas)),
      'unidades_cargadas',
    ).toNumber(),
    aves = number(camada.num_animales_total),
    actuales = Math.max(aves - bajas - cargadas, 0);
  const completos =
      f.salidas.length > 0 && f.salidas.every((s) => s.kg_confirmados_destino != null),
    kgSalidos = sum(f.salidas, 'kg_confirmados_destino').toNumber(),
    nSalidos = sum(f.salidas, 'unidades_cargadas_total').toNumber();
  const pienso = sum(f.entregas, 'kg').toNumber(),
    consumo =
      pienso -
      (camada.cerrada && camada.kg_sobrante_pienso != null ? number(camada.kg_sobrante_pienso) : 0);
  let peso: number | null = null,
    kgProducidos: number | null = null;
  if (camada.cerrada) {
    if (completos && kgSalidos > 0 && nSalidos > 0) {
      peso = kgSalidos / nSalidos;
      kgProducidos = kgSalidos;
    }
  } else {
    const vivos = lotes.filter((l) => l.aves_actuales > 0),
      latest = new Map<number, OfflineRow>();
    for (const p of [...f.pesajes].sort(byDate)) latest.set(number(p.lote_nave_id), p);
    const media = (id: number) => sampleMean(latest.get(id)).toNumber();
    if (
      vivos.reduce((s, l) => s + l.aves_actuales, 0) === actuales &&
      vivos.length &&
      vivos.every((l) => media(number(l.lote_id)) > 0)
    ) {
      const kg = vivos.reduce((s, l) => s + l.aves_actuales * media(number(l.lote_id)), 0);
      peso = kg / vivos.reduce((s, l) => s + l.aves_actuales, 0);
      if (f.salidas.every((s) => s.kg_confirmados_destino != null)) kgProducidos = kg + kgSalidos;
    } else if (
      actuales === 0 &&
      lotes.every((l) => l.aves_actuales === 0) &&
      completos &&
      kgSalidos > 0
    ) {
      peso = kgSalidos / nSalidos;
      kgProducidos = kgSalidos;
    }
  }
  const fechas = [...new Set(f.registros.map((r) => text(r.fecha)))].sort().reverse(),
    recent = new Set(fechas.slice(0, 5));
  const savedResult = row(row(savedLiquidation(bundle, cid)?.snapshot).resultado),
    economicKeys = [
      'bruto_eur',
      'iva_eur',
      'bonificacion_eur',
      'importe_bajas_eur',
      'importe_carga_eur',
      'total_eur',
    ];
  return {
    camada,
    lotes,
    partes_recientes: f.registros
      .filter((r) => recent.has(text(r.fecha)))
      .sort((a, b) => -byDate(a, b)),
    partes_count: fechas.length,
    piensos_recientes: [...f.entregas].sort((a, b) => -byDate(a, b)).slice(0, 5),
    piensos_count: f.entregas.length,
    kpis: {
      aves_iniciales: aves,
      aves_actuales: actuales,
      bajas_acumuladas: bajas,
      mortalidad_pct: aves ? pythonRound((bajas / aves) * 100, 2) : 0,
      dias_engorde:
        dateNumber(camada.fecha_cierre ? text(camada.fecha_cierre).slice(0, 10) : today) -
        dateNumber(camada.fecha_entrada),
      peso_medio_kg: peso,
      indice_conversion:
        consumo > 0 && kgProducidos != null && kgProducidos > 0
          ? pythonRound(consumo / kgProducidos, 3)
          : null,
      pienso_total_kg: pienso,
      kg_sobrante_pienso:
        camada.kg_sobrante_pienso == null ? null : number(camada.kg_sobrante_pienso),
    },
    liquidacion:
      savedResult.total_eur == null
        ? null
        : Object.fromEntries(economicKeys.map((k) => [k, savedResult[k]])),
  };
}
export function byDate(a: OfflineRow, b: OfflineRow): number {
  return text(a.fecha).localeCompare(text(b.fecha)) || number(a.id) - number(b.id);
}
export function sampleMean(p: OfflineRow | undefined): Decimal {
  const values = Array.isArray(p?.pesos_g) ? (p.pesos_g as number[]) : [];
  return values.length
    ? values
        .reduce((a, v) => a.add(v), new Decimal(0))
        .div(values.length)
        .div(1000)
    : new Decimal(0);
}
export function weightCurve(bundle: OfflineBundle, cid: number, today: string) {
  const { camada } = forCamada(bundle, cid),
    sexo = text(camada.sexo),
    historical = new Set(
      bundle.tables.camadas.filter((c) => c.cerrada && c.sexo === sexo).map((c) => c.id),
    ),
    histLotes = new Map(
      bundle.tables.lotes
        .filter((l) => historical.has(number(l.camada_id)))
        .map((l) => [l.id, number(l.camada_id)]),
    );
  const grouped = new Map<number, { sum: number; count: number }>(),
    references = new Set<number>();
  for (const p of bundle.tables.pesajes) {
    const ref = histLotes.get(number(p.lote_nave_id)),
      c = bundle.tables.camadas.find((c) => c.id === ref);
    if (!c || !Array.isArray(p.pesos_g)) continue;
    const age = dateNumber(p.fecha) - dateNumber(c.fecha_entrada);
    if (age < 0 || !(p.pesos_g as unknown[]).length) continue;
    const group = grouped.get(age) ?? { sum: 0, count: 0 };
    group.sum += sampleMean(p).toNumber();
    group.count++;
    grouped.set(age, group);
    references.add(ref!);
  }
  const historicalPoints = [...grouped]
    .sort((a, b) => a[0] - b[0])
    .map(([age, g]) => ({ edad_dias: age, peso_medio_kg: pythonRound(g.sum / g.count, 3) }));
  const adjustments = configuredPoints(bundle.catalogos.curva_peso[sexo]);
  return {
    sexo,
    puntos: historicalPoints.length ? historicalPoints : adjustments,
    edad_actual_dias:
      dateNumber(
        camada.cerrada && camada.fecha_cierre ? text(camada.fecha_cierre).slice(0, 10) : today,
      ) - dateNumber(camada.fecha_entrada),
    origen: historicalPoints.length ? 'historico' : adjustments.length ? 'ajustes' : 'vacio',
    camadas_historico: references.size,
  };
}
function floatInterpolate(points: [number, number][], age: number): number | null {
  if (!points.length || age < points[0][0]) return null;
  if (age >= points.at(-1)![0]) return points.at(-1)![1];
  for (let i = 0; i < points.length - 1; i++) {
    const [a, pa] = points[i],
      [b, pb] = points[i + 1];
    if (a <= age && age <= b) return pa + ((pb - pa) * (age - a)) / (b - a || 1);
  }
  return null;
}
export function feedCurve(bundle: OfflineBundle, cid: number, today: string) {
  const { camada } = forCamada(bundle, cid),
    sexo = text(camada.sexo),
    reference = bundle.tables.camadas.filter((c) => c.cerrada && c.sexo === sexo),
    series = new Map<number, [number, number][]>();
  let ageEnd = 1;
  for (const c of reference) {
    const deliveries = bundle.tables.entregas
        .filter((e) => number(e.camada_id) === c.id)
        .sort(byDate),
      grouped = new Map<number, number>();
    for (const e of deliveries) {
      const age = dateNumber(e.fecha) - dateNumber(c.fecha_entrada) + 1;
      if (age < 1) continue;
      grouped.set(age, (grouped.get(age) ?? 0) + number(e.kg));
      ageEnd = Math.max(ageEnd, age);
    }
    if (grouped.size) {
      if (c.fecha_cierre)
        ageEnd = Math.max(
          ageEnd,
          dateNumber(text(c.fecha_cierre).slice(0, 10)) - dateNumber(c.fecha_entrada) + 1,
        );
      series.set(
        number(c.id),
        [...grouped].sort((a, b) => a[0] - b[0]),
      );
    }
  }
  const grouped = new Map<number, number[]>();
  for (const points of series.values()) {
    let accumulated = 0;
    const milestones = points.map(([age, kg]): [number, number] => [age, (accumulated += kg)]);
    for (let age = 1; age <= Math.max(ageEnd, milestones.at(-1)![0]); age++) {
      const values = grouped.get(age) ?? [];
      values.push(
        age < milestones[0][0] ? 0 : pythonRound(floatInterpolate(milestones, age) ?? 0, 2),
      );
      grouped.set(age, values);
    }
  }
  const points = [...grouped].map(([age, values]) => ({
      edad_dias: age,
      kg_acumulados: pythonRound(values.reduce((a, b) => a + b, 0) / values.length, 2),
    })),
    adjustments = configuredPoints(bundle.catalogos.curva_pienso[sexo]);
  return {
    sexo,
    puntos: points.length ? points : adjustments,
    edad_actual_dias: Math.max(
      dateNumber(
        camada.cerrada && camada.fecha_cierre ? text(camada.fecha_cierre).slice(0, 10) : today,
      ) -
        dateNumber(camada.fecha_entrada) +
        1,
      1,
    ),
    origen: points.length ? 'historico' : adjustments.length ? 'ajustes' : 'vacio',
    camadas_historico: series.size,
  };
}
type WeightReference = { points: [number, Decimal][]; ids: number[] };
function referenceAt(reference: WeightReference, age: number): Decimal | null {
  const ps = reference.points;
  if (!ps.length || age < ps[0][0] || age > ps.at(-1)![0]) return null;
  for (const [a, p] of ps) if (age === a) return p;
  for (let i = 0; i < ps.length - 1; i++) {
    const [a, pa] = ps[i],
      [b, pb] = ps[i + 1];
    if (a < age && age < b)
      return pa.add(
        pb
          .sub(pa)
          .mul(age - a)
          .div(b - a),
      );
  }
  return null;
}
function evolutionReference(bundle: OfflineBundle, camada: OfflineRow): WeightReference {
  const historical = new Map(
    bundle.tables.camadas
      .filter(
        (c) =>
          c.id !== camada.id &&
          c.cerrada &&
          c.sexo === camada.sexo &&
          c.fecha_cierre &&
          madridDate(text(c.fecha_cierre)) < text(camada.fecha_entrada),
      )
      .map((c) => [c.id, c]),
  );
  const lotes = new Map(
    bundle.tables.lotes
      .filter((l) => historical.has(number(l.camada_id)))
      .map((l) => [l.id, historical.get(number(l.camada_id))!]),
  );
  const grouped = new Map<number, { total: Decimal; count: number }>(),
    refs = new Set<number>();
  for (const p of bundle.tables.pesajes) {
    const c = lotes.get(number(p.lote_nave_id));
    if (
      !c ||
      text(p.fecha) >= text(camada.fecha_entrada) ||
      !Array.isArray(p.pesos_g) ||
      !p.pesos_g.length
    )
      continue;
    const age = dateNumber(p.fecha) - dateNumber(c.fecha_entrada);
    if (age < 0) continue;
    const g = grouped.get(age) ?? { total: new Decimal(0), count: 0 };
    g.total = g.total.add(sampleMean(p));
    g.count++;
    grouped.set(age, g);
    refs.add(number(c.id));
  }
  return {
    points: [...grouped].sort((a, b) => a[0] - b[0]).map(([age, g]) => [age, g.total.div(g.count)]),
    ids: [...refs].sort((a, b) => a - b),
  };
}
export function evolution(
  bundle: OfflineBundle,
  cid: number,
  today: string,
  requestedUntil?: string,
): EvolucionLiquidacion {
  const f = forCamada(bundle, cid),
    c = f.camada,
    config = bundle.catalogos.liquidacion as ConfiguracionLiquidacion,
    start = dateNumber(c.fecha_entrada);
  const lastExit = f.salidas
    .map((s) => text(s.fecha_carga))
    .sort()
    .at(-1);
  if (c.cerrada && !lastExit)
    throw new Error('No hay una salida válida que delimite la evolución.');
  const limit = c.cerrada && lastExit && lastExit < today ? lastExit : today,
    until = requestedUntil ?? limit,
    end = dateNumber(until),
    days = end - start + 1;
  if (until > limit || days < 1 || days > 1000)
    throw new Error('La fecha debe estar dentro de la camada y el periodo no superar 1.000 días.');
  const historical = evolutionReference(bundle, c),
    adjustments: WeightReference = {
      points: configuredPoints(bundle.catalogos.curva_peso[text(c.sexo)])
        .map((p): [number, Decimal] => [number(p.edad_dias), decimal(p.peso_medio_kg)])
        .sort((a, b) => a[0] - b[0]),
      ids: [],
    };
  const groups = (records: OfflineRow[], field = 'fecha') => {
    const map = new Map<string, OfflineRow[]>();
    for (const r of records) {
      const key = text(r[field]);
      map.set(key, [...(map.get(key) ?? []), r]);
    }
    return map;
  };
  const entradas = groups(f.entradas),
    registros = groups(f.registros),
    pesajes = groups([...f.pesajes].sort(byDate)),
    entregas = groups([...f.entregas].sort(byDate)),
    salidas = groups(f.salidas, 'fecha_carga');
  const lastEntry = [...entradas.keys()].sort().at(-1) ?? text(c.fecha_entrada),
    coverage = entradas.size <= 1,
    bajas = new Map<number, number>(),
    cargadas = new Map<number, number>(),
    samples = new Map<number, OfflineRow>();
  let entered = 0,
    exited = 0,
    pending = 0,
    missingExitWeight = 0,
    kgEntry = new Decimal(0),
    kgExits = new Decimal(0),
    feed = new Decimal(0),
    lastFeed: OfflineRow | undefined,
    invalidExit = false;
  const serie: DiaEvolucion[] = [];
  for (let i = 0; i < days; i++) {
    const fecha = dateAt(start + i),
      causas: string[] = [],
      advertencias = ['pienso_entregado_no_consumo'];
    const count = (l: OfflineRow) =>
        number(l.num_animales_asignados) -
        (bajas.get(number(l.id)) ?? 0) -
        (cargadas.get(number(l.id)) ?? 0),
      initial = new Map(f.lotes.map((l) => [l.id, count(l)]));
    for (const e of entradas.get(fecha) ?? []) {
      entered += number(e.num_animales);
      kgEntry = kgEntry.add(decimal(e.kg_totales));
    }
    const parts = registros.get(fecha) ?? [],
      present = new Set(parts.map((r) => number(r.lote_nave_id)));
    if (coverage && fecha >= lastEntry)
      pending += f.lotes.filter(
        (l) => number(initial.get(l.id)) > 0 && !present.has(number(l.id)),
      ).length;
    for (const r of parts) {
      const id = number(r.lote_nave_id);
      bajas.set(id, (bajas.get(id) ?? 0) + number(r.bajas_muerte) + number(r.bajas_triaje));
    }
    for (const p of pesajes.get(fecha) ?? []) samples.set(number(p.lote_nave_id), p);
    for (const e of entregas.get(fecha) ?? []) {
      feed = feed.add(decimal(e.kg));
      lastFeed = e;
    }
    for (const s of salidas.get(fecha) ?? []) {
      exited += number(s.unidades_cargadas_total);
      if (s.kg_confirmados_destino == null) missingExitWeight++;
      else kgExits = kgExits.add(decimal(s.kg_confirmados_destino));
      const lines = rows(s.lineas);
      if (sum(lines, 'unidades_cargadas').toNumber() !== number(s.unidades_cargadas_total))
        invalidExit = true;
      for (const line of lines) {
        const id = number(line.lote_nave_id);
        if (!f.lotes.some((l) => l.id === id)) invalidExit = true;
        cargadas.set(id, (cargadas.get(id) ?? 0) + number(line.unidades_cargadas));
      }
    }
    const deaths = [...bajas.values()].reduce((a, b) => a + b, 0),
      remaining = entered - deaths - exited,
      perLot = f.lotes.map((l) => count(l));
    if (!entered) causas.push('sin_entradas');
    if (fecha < lastEntry) causas.push('reparto_historico_no_disponible');
    if (
      remaining < 0 ||
      invalidExit ||
      (fecha >= lastEntry &&
        (perLot.some((n) => n < 0) || perLot.reduce((a, b) => a + b, 0) !== remaining))
    )
      causas.push('descuadre_animales');
    if (c.edad_dias_entrada == null) causas.push('edad_entrada_pendiente');
    if (!lastFeed) causas.push('pienso_pendiente');
    if (missingExitWeight) causas.push('peso_salida_pendiente');
    let weights: PesoEvolucion[] = [],
      estimated: Decimal | null = null;
    if (fecha >= lastEntry && !causas.includes('descuadre_animales') && entered) {
      weights = f.lotes
        .filter((l) => count(l) > 0)
        .map((l) => {
          const sample = samples.get(number(l.id));
          let weight: Decimal | null = null,
            origin: PesoEvolucion['origen'] = 'pendiente',
            ids: number[] = [];
          if (sample && Array.isArray(sample.pesos_g) && sample.pesos_g.length) {
            const base = sampleMean(sample);
            if (sample.fecha === fecha) {
              weight = base;
              origin = 'muestreo';
            } else
              for (const [name, ref] of [
                ['historico', historical],
                ['ajustes', adjustments],
              ] as const) {
                const a = referenceAt(ref, dateNumber(sample.fecha) - start),
                  b = referenceAt(ref, start + i - start);
                if (a && b && a.compare(0) > 0 && b.compare(a) >= 0) {
                  weight = base.add(b).sub(a);
                  origin = name;
                  ids = ref.ids;
                  break;
                }
              }
          }
          if (weight && weight.compare(0) <= 0) {
            weight = null;
            origin = 'pendiente';
          }
          return {
            lote_nave_id: number(l.id),
            nave_id: number(l.nave_id),
            nave_codigo: text(
              bundle.tables.naves.find((n) => n.id === l.nave_id)?.codigo ?? l.nave_codigo,
            ),
            aves_restantes: count(l),
            fecha_muestreo: sample ? text(sample.fecha) : null,
            antiguedad_dias: sample ? start + i - dateNumber(sample.fecha) : null,
            peso_medio_kg: weight?.toString() ?? null,
            kg_vivos: weight?.mul(count(l)).toString() ?? null,
            origen: origin,
            camadas_referencia: ids,
          };
        });
      if (weights.some((w) => w.kg_vivos == null))
        causas.push(
          weights.some((w) => w.fecha_muestreo == null)
            ? 'peso_pendiente'
            : 'curva_fuera_cobertura',
        );
      else if (!missingExitWeight) {
        estimated = kgExits.add(weights.reduce((a, w) => a.add(w.kg_vivos ?? 0), new Decimal(0)));
        if (estimated.compare(kgEntry) <= 0) causas.push('kg_ganados_no_positivos');
      }
    }
    if (pending) advertencias.push('bajas_incompletas');
    if (!coverage) advertencias.push('cobertura_no_determinable');
    const resultado =
      !causas.length && estimated
        ? calculateLiquidation(
            {
              sexo: text(c.sexo) as DatosLiquidacion['sexo'],
              fecha_entrada: text(c.fecha_entrada),
              fecha_final: fecha,
              edad_entrada: number(c.edad_dias_entrada),
              aves_entradas: entered,
              bajas_registradas: deaths,
              kg_entrada: kgEntry.toString(),
              kg_salida: estimated.toString(),
              kg_pienso: feed.toString(),
              ajuste_carga_eur: decimal(c.ajuste_carga_inicial_eur).toString(),
              bonificacion_eur: decimal(c.bonificacion_inicial_eur).toString(),
            },
            config,
          )
        : null;
    serie.push({
      fecha,
      aves_entradas: entered,
      bajas_registradas: deaths,
      aves_salidas: exited,
      aves_restantes: remaining,
      kg_entrada: kgEntry.toString(),
      kg_salidas_confirmadas: kgExits.toString(),
      kg_salida_simulado: estimated?.toString() ?? null,
      kg_pienso: lastFeed ? feed.toString() : null,
      fecha_ultima_entrega: lastFeed ? text(lastFeed.fecha) : null,
      kg_ultima_entrega: lastFeed ? decimal(lastFeed.kg).toString() : null,
      pesos: weights,
      partes_pendientes: pending,
      cobertura_determinable: coverage,
      comparable: resultado !== null && coverage && pending === 0,
      causas,
      advertencias,
      resultado,
    });
  }
  const ventanas: VentanaEvolucion[] = [];
  for (let i = 7; i < serie.length; i++)
    if (serie.slice(i - 7, i + 1).every((d) => d.comparable)) {
      const a = serie[i - 7],
        b = serie[i];
      ventanas.push({
        desde: a.fecha,
        hasta: b.fecha,
        variacion_total_eur: decimal(b.resultado!.total_eur).sub(a.resultado!.total_eur).toString(),
        bajas_periodo: b.bajas_registradas - a.bajas_registradas,
        kg_entregados_periodo: decimal(b.kg_pienso).sub(decimal(a.kg_pienso)).toString(),
      });
    }
  const worst =
    [...ventanas].sort(
      (a, b) =>
        decimal(a.variacion_total_eur).compare(b.variacion_total_eur) ||
        a.hasta.localeCompare(b.hasta),
    )[0] ?? null;
  return {
    camada_id: cid,
    codigo_lote: text(c.codigo_lote),
    fecha_calculo: today,
    fecha_desde: text(c.fecha_entrada),
    fecha_hasta: until,
    metodo_pienso: 'entregado_acumulado',
    configuracion: config,
    serie,
    ventanas,
    peor_ventana: worst,
  };
}
export function liquidationSnapshot(value: unknown): LiquidacionSnapshot {
  const r = row(value);
  return {
    datos: r.datos,
    fuentes: r.fuentes,
    configuracion: r.configuracion,
    resultado: r.resultado,
  } as LiquidacionSnapshot;
}
